export const YES_NO_OPTIONS = ["", "Yes", "No"];

export const TREATMENT_CATEGORIES = [
  "Procedure",
  "Intervention",
  "Assessment",
  "Medication",
];

export const MEDICATION_OPTIONS = [
  "Adenosine",
  "Amiodarone",
  "Aspirin",
  "Atropine",
  "Bacitracin",
  "Benadryl",
  "Calcium Chloride",
  "Cardizem",
  "D10",
  "D50",
  "Dexamethasone",
  "Dopamine",
  "DuoNeb",
  "Epinephrine 1:1,000",
  "Epinephrine 1:10,000",
  "Flumazenil",
  "Glucagon",
  "Glucose, Oral",
  "Haldol",
  "Hydrocortisone Cream",
  "Ibuprofen",
  "Lasix",
  "Levophed",
  "Lidocaine 2%",
  "Loperamide",
  "Magnesium Sulfate",
  "Metoprolol",
  "Narcan",
  "Nitroglycerin",
  "Oxytocin",
  "Phenylephrine",
  "Sodium Bicarb",
  "Solu-Medrol",
  "Tylenol",
  "Zofran IV",
  "Zofran ODT",
];

export const MEDICATION_DEFAULTS = {
  Adenosine: {
    dose: "6 mg",
    routes: ["IV"],
    indications: ["SVT"],
  },
  Amiodarone: {
    dose: "150 mg",
    routes: ["IV", "IO"],
    indications: ["Wide complex tachycardia", "V-fib / V-tach"],
  },
  Aspirin: {
    dose: "324 mg",
    routes: ["PO"],
    indications: ["Chest pain", "Suspected ACS"],
  },
  Atropine: {
    dose: "1 mg",
    routes: ["IV", "IO"],
    indications: ["Bradycardia"],
  },
  Benadryl: {
    dose: "25 mg",
    routes: ["PO", "IM", "IV"],
    indications: ["Allergic reaction", "Anaphylaxis"],
  },
  "Calcium Chloride": {
    dose: "1 g",
    routes: ["IV", "IO"],
    indications: ["Hyperkalemia", "Calcium channel blocker overdose"],
  },
  D10: {
    dose: "250 mL",
    routes: ["IV", "IO"],
    indications: ["Hypoglycemia"],
  },
  D50: {
    dose: "25 g",
    routes: ["IV", "IO"],
    indications: ["Hypoglycemia"],
  },
  Dexamethasone: {
    dose: "10 mg",
    routes: ["PO", "IM", "IV"],
    indications: ["Allergic reaction", "Asthma", "Croup"],
  },
  DuoNeb: {
    dose: "1 neb",
    routes: ["Neb"],
    indications: ["Wheezing", "Asthma", "COPD"],
  },
  "Epinephrine 1:1,000": {
    dose: "0.3 mg",
    routes: ["IM"],
    indications: ["Anaphylaxis"],
  },
  "Epinephrine 1:10,000": {
    dose: "1 mg",
    routes: ["IV", "IO"],
    indications: ["Cardiac arrest"],
  },
  Glucagon: {
    dose: "1 mg",
    routes: ["IM", "IN"],
    indications: ["Hypoglycemia"],
  },
  "Glucose, Oral": {
    dose: "15 g",
    routes: ["PO"],
    indications: ["Hypoglycemia"],
  },
  Ibuprofen: {
    dose: "400 mg",
    routes: ["PO"],
    indications: ["Pain", "Fever"],
  },
  Narcan: {
    dose: "2 mg",
    routes: ["IN", "IM", "IV"],
    indications: ["Overdose", "Respiratory depression"],
  },
  Nitroglycerin: {
    dose: "0.4 mg",
    routes: ["SL"],
    indications: ["Chest pain"],
  },
  "Solu-Medrol": {
    dose: "125 mg",
    routes: ["IV", "IM"],
    indications: ["Allergic reaction", "Asthma", "COPD"],
  },
  Tylenol: {
    dose: "1000 mg",
    routes: ["PO"],
    indications: ["Pain", "Fever"],
  },
  "Zofran IV": {
    dose: "4 mg",
    routes: ["IV", "IM"],
    indications: ["Nausea", "Vomiting"],
  },
  "Zofran ODT": {
    dose: "4 mg",
    routes: ["PO"],
    indications: ["Nausea", "Vomiting"],
  },
};

export const TREATMENT_SCHEMAS = {
  "IV Start": {
    category: "Procedure",
    type: "IV Start",
    label: "IV Start",
    detailFields: [
      { name: "cathSize", label: "IV Cath Size", type: "select", options: ["", "24g", "22g", "20g", "18g", "16g", "14g"] },
      { name: "site", label: "IV Site", type: "select", options: ["", "L Hand", "R Hand", "L Forearm", "R Forearm", "L AC", "R AC", "L EJ", "R EJ", "Other"] },
      { name: "fluidType", label: "Fluid Type", type: "select", options: ["", "NS", "LR", "D5W", "None", "Other"] },
      { name: "fluidVolume", label: "Fluid Volume", type: "text", placeholder: "e.g. 500", suffix: "mL" },
    ],
    summaryFields: ["cathSize", "site", "fluidType", "fluidVolume"],
  },

  "IV Removal": {
    category: "Procedure",
    type: "IV Removal",
    label: "IV Removal",
    detailFields: [
      { name: "site", label: "IV Site", type: "select", options: ["", "L Hand", "R Hand", "L Forearm", "R Forearm", "L AC", "R AC", "L EJ", "R EJ", "Other"] },
      { name: "catheterIntact", label: "Catheter Intact", type: "select", options: YES_NO_OPTIONS },
      { name: "dressingApplied", label: "Dressing Applied", type: "select", options: YES_NO_OPTIONS },
    ],
    summaryFields: ["site", "catheterIntact", "dressingApplied"],
  },

  "Fluid Bolus": {
    category: "Procedure",
    type: "Fluid Bolus",
    label: "Fluid Bolus",
    detailFields: [
      { name: "fluidType", label: "Fluid Type", type: "select", options: ["", "NS", "LR", "D5W", "Other"] },
      { name: "fluidVolume", label: "Volume", type: "text", placeholder: "e.g. 500", suffix: "mL" },
      { name: "route", label: "Route", type: "select", options: ["", "IV", "IO"] },
    ],
    summaryFields: ["fluidType", "fluidVolume", "route"],
  },

  "IO Placement": {
    category: "Procedure",
    type: "IO Placement",
    label: "IO Placement",
    detailFields: [
      { name: "site", label: "IO Site", type: "select", options: ["", "L Tibia", "R Tibia", "L Humeral Head", "R Humeral Head", "Other"] },
      { name: "needleSize", label: "Needle Size", type: "select", options: ["", "15mm", "25mm", "45mm", "Other"] },
      { name: "successful", label: "Successful", type: "select", options: YES_NO_OPTIONS },
    ],
    summaryFields: ["site", "needleSize", "successful"],
  },

  Oxygen: {
    category: "Intervention",
    type: "Oxygen",
    label: "Oxygen",
    detailFields: [
      { name: "device", label: "Device", type: "select", options: ["", "NC", "Simple Mask", "NRB", "BVM", "CPAP", "Other"] },
      { name: "flowRate", label: "Flow Rate", type: "text", placeholder: "e.g. 4", suffix: "LPM" },
    ],
    summaryFields: ["device", "flowRate"],
  },

  BVM: {
    category: "Procedure",
    type: "BVM",
    label: "BVM",
    detailFields: [
      { name: "airwayAdjunct", label: "Airway Adjunct", type: "select", options: ["", "None", "OPA", "NPA", "SGA", "ETT", "Other"] },
      { name: "o2FlowRate", label: "O2 Flow Rate", type: "text", placeholder: "e.g. 15", suffix: "LPM" },
      { name: "chestRise", label: "Effective Chest Rise", type: "select", options: YES_NO_OPTIONS },
    ],
    summaryFields: ["airwayAdjunct", "o2FlowRate", "chestRise"],
  },

  "Airway Adjunct Placement": {
    category: "Procedure",
    type: "Airway Adjunct Placement",
    label: "Airway Adjunct Placement",
    detailFields: [
      { name: "adjunct", label: "Adjunct", type: "select", options: ["", "OPA", "NPA", "iGel / SGA", "Other"] },
      { name: "size", label: "Size", type: "text", placeholder: "e.g. 4" },
      { name: "tolerated", label: "Tolerated", type: "select", options: YES_NO_OPTIONS },
    ],
    summaryFields: ["adjunct", "size", "tolerated"],
  },

  Suctioning: {
    category: "Procedure",
    type: "Suctioning",
    label: "Suctioning",
    detailFields: [
      { name: "route", label: "Route", type: "select", options: ["", "Oral", "Nasal", "Tracheal", "Other"] },
      { name: "secretions", label: "Secretions", type: "select", options: ["", "Clear", "Bloody", "Vomitus", "Thick", "Other"] },
      { name: "effective", label: "Effective", type: "select", options: YES_NO_OPTIONS },
    ],
    summaryFields: ["route", "secretions", "effective"],
  },

  CPAP: {
    category: "Procedure",
    type: "CPAP",
    label: "CPAP",
    detailFields: [
      { name: "pressure", label: "Pressure", type: "text", placeholder: "e.g. 5", suffix: "cmH₂O" },
      { name: "o2FlowRate", label: "O2 Flow Rate", type: "text", placeholder: "e.g. 15", suffix: "LPM" },
      { name: "tolerated", label: "Tolerated", type: "select", options: YES_NO_OPTIONS },
    ],
    summaryFields: ["pressure", "o2FlowRate", "tolerated"],
  },

  CPR: {
    category: "Procedure",
    type: "CPR",
    label: "CPR",
    detailFields: [
      { name: "startTime", label: "Start Time", type: "text", placeholder: "e.g. 12:44" },
      { name: "endTime", label: "End Time", type: "text", placeholder: "e.g. 12:58" },
      { name: "compressions", label: "Compressions", type: "select", options: ["", "Manual", "Mechanical"] },
      { name: "rosc", label: "ROSC", type: "select", options: YES_NO_OPTIONS },
    ],
    summaryFields: ["startTime", "endTime", "compressions", "rosc"],
  },

  "Mechanical CPR": {
    category: "Procedure",
    type: "Mechanical CPR",
    label: "Mechanical CPR",
    detailFields: [
      { name: "device", label: "Device", type: "select", options: ["", "LUCAS", "AutoPulse", "Other"] },
      { name: "startTime", label: "Start Time", type: "text", placeholder: "e.g. 12:44" },
      { name: "endTime", label: "End Time", type: "text", placeholder: "e.g. 12:58" },
      { name: "rosc", label: "ROSC", type: "select", options: YES_NO_OPTIONS },
    ],
    summaryFields: ["device", "startTime", "endTime", "rosc"],
  },

  Intubation: {
    category: "Procedure",
    type: "Intubation",
    label: "Intubation",
    detailFields: [
      { name: "tubeSize", label: "Tube Size", type: "text", placeholder: "e.g. 7.5", suffix: "mm" },
      { name: "bladeSize", label: "Blade Size", type: "text", placeholder: "e.g. Mac 4" },
      { name: "video", label: "Video", type: "select", options: YES_NO_OPTIONS },
      { name: "numberAtLips", label: "Number at Lips", type: "text", placeholder: "e.g. 22", suffix: "cm" },
      { name: "visualizedCords", label: "Visualized Cords", type: "select", options: YES_NO_OPTIONS },
      { name: "lungSounds", label: "Bilateral Lung Sounds", type: "select", options: YES_NO_OPTIONS },
      { name: "epigastricSoundsAbsent", label: "Epigastric Sounds Absent", type: "select", options: YES_NO_OPTIONS },
      { name: "etco2Confirmed", label: "ETCO2 Confirmed", type: "select", options: YES_NO_OPTIONS },
    ],
    summaryFields: ["tubeSize", "bladeSize", "video", "numberAtLips", "etco2Confirmed"],
  },

  "12-Lead ECG": {
    category: "Procedure",
    type: "12-Lead ECG",
    label: "12-Lead ECG",
    detailFields: [
      { name: "interpretation", label: "Interpretation", type: "text", placeholder: "e.g. NSR, STEMI alert, etc." },
      { name: "transmitted", label: "Transmitted", type: "select", options: YES_NO_OPTIONS },
      { name: "attached", label: "ECG Attached", type: "select", options: YES_NO_OPTIONS },
    ],
    summaryFields: ["interpretation", "transmitted", "attached"],
  },

  "Cardiac Monitoring": {
    category: "Procedure",
    type: "Cardiac Monitoring",
    label: "Cardiac Monitoring",
    detailFields: [
      { name: "rhythm", label: "Rhythm", type: "text", placeholder: "e.g. NSR" },
      { name: "monitoringContinued", label: "Monitoring Continued", type: "select", options: YES_NO_OPTIONS },
    ],
    summaryFields: ["rhythm", "monitoringContinued"],
  },

  Defibrillation: {
    category: "Procedure",
    type: "Defibrillation",
    label: "Defibrillation",
    detailFields: [
      { name: "energy", label: "Energy", type: "text", placeholder: "e.g. 200", suffix: "J" },
      { name: "numberOfShocks", label: "Number of Shocks", type: "text", placeholder: "e.g. 1" },
      { name: "rhythmChange", label: "Rhythm Change", type: "select", options: YES_NO_OPTIONS },
    ],
    summaryFields: ["energy", "numberOfShocks", "rhythmChange"],
  },

  Cardioversion: {
    category: "Procedure",
    type: "Cardioversion",
    label: "Cardioversion",
    detailFields: [
      { name: "energy", label: "Energy", type: "text", placeholder: "e.g. 100", suffix: "J" },
      { name: "synchronized", label: "Synchronized", type: "select", options: YES_NO_OPTIONS },
      { name: "rhythmChange", label: "Rhythm Change", type: "select", options: YES_NO_OPTIONS },
    ],
    summaryFields: ["energy", "synchronized", "rhythmChange"],
  },

  Pacing: {
    category: "Procedure",
    type: "Pacing",
    label: "Pacing",
    detailFields: [
      { name: "rate", label: "Rate", type: "text", placeholder: "e.g. 70" },
      { name: "milliamps", label: "mA", type: "text", placeholder: "e.g. 80" },
      { name: "capture", label: "Capture", type: "select", options: YES_NO_OPTIONS },
    ],
    summaryFields: ["rate", "milliamps", "capture"],
  },

  "Cooling Measures": {
    category: "Intervention",
    type: "Cooling Measures",
    label: "Cooling Measures",
    detailFields: [
      { name: "method", label: "Method", type: "multiSelect", options: ["Ice Packs", "Cold Towels", "Mist/Fan", "Shade", "Immersion", "Other"] },
      { name: "location", label: "Applied To", type: "text", placeholder: "e.g. neck/axilla/groin" },
      { name: "fluidsGiven", label: "Fluids Given", type: "select", options: YES_NO_OPTIONS },
    ],
    summaryFields: ["method", "location", "fluidsGiven"],
  },

  "External Warming": {
    category: "Intervention",
    type: "External Warming",
    label: "External Warming",
    detailFields: [
      { name: "method", label: "Method", type: "multiSelect", options: ["Blankets", "Warm Packs", "Moved Indoors", "Other"] },
      { name: "response", label: "Response", type: "text", placeholder: "Patient response" },
    ],
    summaryFields: ["method", "response"],
  },

  "Ice / Cold Pack": {
    category: "Intervention",
    type: "Ice / Cold Pack",
    label: "Ice / Cold Pack",
    detailFields: [
      { name: "location", label: "Applied To", type: "text", placeholder: "e.g. R ankle" },
      { name: "tolerated", label: "Tolerated", type: "select", options: YES_NO_OPTIONS },
    ],
    summaryFields: ["location", "tolerated"],
  },

  "Oral Hydration": {
    category: "Intervention",
    type: "Oral Hydration",
    label: "Oral Hydration",
    detailFields: [
      { name: "fluid", label: "Fluid", type: "select", options: ["", "Water", "Sports Drink", "Oral Rehydration Solution", "Other"] },
      { name: "amount", label: "Amount", type: "text", placeholder: "e.g. 16", suffix: "oz" },
      { name: "tolerated", label: "Tolerated", type: "select", options: YES_NO_OPTIONS },
    ],
    summaryFields: ["fluid", "amount", "tolerated"],
  },

  "Burn Care": {
    category: "Intervention",
    type: "Burn Care",
    label: "Burn Care",
    detailFields: [
      { name: "location", label: "Burn Location", type: "text", placeholder: "e.g. L forearm" },
      { name: "care", label: "Care Provided", type: "multiSelect", options: ["Cooled", "Dressed", "Burn Gel", "Covered", "Other"] },
      { name: "dressingApplied", label: "Dressing Applied", type: "select", options: YES_NO_OPTIONS },
    ],
    summaryFields: ["location", "care", "dressingApplied"],
  },

  Repositioning: {
    category: "Intervention",
    type: "Repositioning",
    label: "Repositioning",
    detailFields: [
      { name: "position", label: "Position", type: "select", options: ["", "Supine", "Sitting", "Semi-Fowler", "Recovery Position", "Position of Comfort", "Other"] },
      { name: "reason", label: "Reason", type: "text", placeholder: "e.g. comfort, airway, hypotension" },
    ],
    summaryFields: ["position", "reason"],
  },

  "Transfer of Care": {
    category: "Intervention",
    type: "Transfer of Care",
    label: "Transfer of Care",
    detailFields: [
      { name: "transferredTo", label: "Transferred To", type: "text", placeholder: "e.g. EMS, ED, parent, self" },
      { name: "reportGiven", label: "Report Given", type: "select", options: YES_NO_OPTIONS },
      { name: "documentsSent", label: "Documents Sent", type: "select", options: YES_NO_OPTIONS },
    ],
    summaryFields: ["transferredTo", "reportGiven", "documentsSent"],
  },

  "Refusal Counseling": {
    category: "Intervention",
    type: "Refusal Counseling",
    label: "Refusal Counseling",
    detailFields: [
      { name: "risksExplained", label: "Risks Explained", type: "select", options: YES_NO_OPTIONS },
      { name: "advised911", label: "Advised to Call 911 / Seek Care", type: "select", options: YES_NO_OPTIONS },
      { name: "understands", label: "Patient Understands", type: "select", options: YES_NO_OPTIONS },
    ],
    summaryFields: ["risksExplained", "advised911", "understands"],
  },

  "Isolation Precautions": {
    category: "Intervention",
    type: "Isolation Precautions",
    label: "Isolation Precautions",
    detailFields: [
      { name: "precautionType", label: "Precaution Type", type: "multiSelect", options: ["Contact", "Droplet", "Airborne", "Standard", "Other"] },
      { name: "ppeUsed", label: "PPE Used", type: "multiSelect", options: ["Gloves", "Mask", "Eye Protection", "Gown", "N95", "Other"] },
    ],
    summaryFields: ["precautionType", "ppeUsed"],
  },

  Decontamination: {
    category: "Intervention",
    type: "Decontamination",
    label: "Decontamination",
    detailFields: [
      { name: "type", label: "Type", type: "select", options: ["", "Gross Decon", "Eye Flush", "Skin Wash", "Clothing Removed", "Other"] },
      { name: "completed", label: "Completed", type: "select", options: YES_NO_OPTIONS },
    ],
    summaryFields: ["type", "completed"],
  },

  Splint: {
    category: "Procedure",
    type: "Splint",
    label: "Splint",
    detailFields: [
      { name: "splintType", label: "Splint Type", type: "select", options: ["", "SAM", "Air Splint", "Vacuum", "Rigid", "Soft", "Other"] },
      { name: "site", label: "Site", type: "text", placeholder: "e.g. R ankle" },
      { name: "cmsIntact", label: "CMS Intact", type: "select", options: YES_NO_OPTIONS },
    ],
    summaryFields: ["splintType", "site", "cmsIntact"],
  },

  "Cervical Collar": {
    category: "Procedure",
    type: "Cervical Collar",
    label: "Cervical Collar",
    detailFields: [
      { name: "size", label: "Size", type: "select", options: ["", "Pediatric", "No-Neck", "Short", "Regular", "Tall", "Other"] },
      { name: "cmsIntact", label: "CMS Intact After Application", type: "select", options: YES_NO_OPTIONS },
    ],
    summaryFields: ["size", "cmsIntact"],
  },

  "Pelvic Binder": {
    category: "Procedure",
    type: "Pelvic Binder",
    label: "Pelvic Binder",
    detailFields: [
      { name: "device", label: "Device", type: "text", placeholder: "e.g. SAM Sling" },
      { name: "cmsIntact", label: "CMS Intact After Application", type: "select", options: YES_NO_OPTIONS },
    ],
    summaryFields: ["device", "cmsIntact"],
  },

  "Pressure Dressing": {
    category: "Procedure",
    type: "Pressure Dressing",
    label: "Pressure Dressing",
    detailFields: [
      { name: "location", label: "Location", type: "text", placeholder: "e.g. L forearm" },
      { name: "bleedingControlled", label: "Bleeding Controlled", type: "select", options: YES_NO_OPTIONS },
    ],
    summaryFields: ["location", "bleedingControlled"],
  },

  "Hemostatic Dressing": {
    category: "Procedure",
    type: "Hemostatic Dressing",
    label: "Hemostatic Dressing",
    detailFields: [
      { name: "location", label: "Location", type: "text", placeholder: "e.g. R thigh" },
      { name: "agent", label: "Agent", type: "select", options: ["", "QuikClot", "Celox", "Combat Gauze", "Other"] },
      { name: "bleedingControlled", label: "Bleeding Controlled", type: "select", options: YES_NO_OPTIONS },
    ],
    summaryFields: ["location", "agent", "bleedingControlled"],
  },

  Tourniquet: {
    category: "Procedure",
    type: "Tourniquet",
    label: "Tourniquet",
    detailFields: [
      { name: "location", label: "Location", type: "text", placeholder: "e.g. R upper arm" },
      { name: "device", label: "Device", type: "select", options: ["", "CAT", "SOFTT-W", "Improvised", "Other"] },
      { name: "timeApplied", label: "Time Applied", type: "text", placeholder: "e.g. 14:05" },
      { name: "bleedingControlled", label: "Bleeding Controlled", type: "select", options: YES_NO_OPTIONS },
    ],
    summaryFields: ["location", "device", "timeApplied", "bleedingControlled"],
  },

  "Wound Irrigation": {
    category: "Procedure",
    type: "Wound Irrigation",
    label: "Wound Irrigation",
    detailFields: [
      { name: "location", label: "Location", type: "text", placeholder: "e.g. L hand" },
      { name: "solution", label: "Solution", type: "select", options: ["", "NS", "Sterile Water", "Tap Water", "Other"] },
      { name: "volume", label: "Volume", type: "text", placeholder: "e.g. 250", suffix: "mL" },
    ],
    summaryFields: ["location", "solution", "volume"],
  },

  "Eye Irrigation": {
    category: "Procedure",
    type: "Eye Irrigation",
    label: "Eye Irrigation",
    detailFields: [
      { name: "eye", label: "Eye", type: "select", options: ["", "Left", "Right", "Both"] },
      { name: "solution", label: "Solution", type: "select", options: ["", "NS", "Sterile Water", "Other"] },
      { name: "volume", label: "Volume", type: "text", placeholder: "e.g. 500", suffix: "mL" },
    ],
    summaryFields: ["eye", "solution", "volume"],
  },

  "Wound Care": {
    category: "Procedure",
    type: "Wound Care",
    label: "Wound Care",
    detailFields: [
      { name: "woundLocation", label: "Location", type: "text", placeholder: "e.g. L forearm" },
      {
        name: "careType",
        label: "Care Type",
        type: "multiSelect",
        options: ["Cleaned", "Irrigated", "Dressed", "Bandaged", "Antibiotic Ointment", "Pressure Dressing", "Other"],
      },
      { name: "bleedingControlled", label: "Bleeding Controlled", type: "select", options: YES_NO_OPTIONS },
    ],
    summaryFields: ["woundLocation", "careType", "bleedingControlled"],
  },

  "Wound Closure": {
    category: "Procedure",
    type: "Wound Closure",
    label: "Wound Closure",
    detailFields: [
      { name: "location", label: "Location", type: "text", placeholder: "e.g. L eyebrow" },
      { name: "length", label: "Wound Length", type: "text", placeholder: "e.g. 2", suffix: "cm" },
      {
        name: "closureMethod",
        label: "Closure Method",
        type: "multiSelect",
        options: ["Sutures", "Staples", "Skin Glue", "Steri-Strips", "Butterfly Closure", "Combination", "Other"],
      },
      {
        name: "sutureSize",
        label: "Suture Size",
        type: "select",
        options: ["", "2-0", "3-0", "4-0", "5-0", "6-0", "Other"],
        showWhen: { field: "closureMethod", includes: "Sutures" },
      },
      {
        name: "sutureType",
        label: "Suture Type",
        type: "select",
        options: ["", "Nylon", "Prolene", "Vicryl", "Chromic", "Fast Gut", "Silk", "Other"],
        showWhen: { field: "closureMethod", includes: "Sutures" },
      },
      {
        name: "numberOfSutures",
        label: "Number of Sutures",
        type: "text",
        placeholder: "e.g. 4",
        showWhen: { field: "closureMethod", includes: "Sutures" },
      },
      {
        name: "numberOfStaples",
        label: "Number of Staples",
        type: "text",
        placeholder: "e.g. 6",
        showWhen: { field: "closureMethod", includes: "Staples" },
      },
      {
        name: "glueType",
        label: "Glue Type",
        type: "select",
        options: ["", "Dermabond", "Skin Affix", "Other"],
        showWhen: { field: "closureMethod", includes: "Skin Glue" },
      },
      {
        name: "numberOfStrips",
        label: "Number of Steri-Strips",
        type: "text",
        placeholder: "e.g. 3",
        showWhen: { field: "closureMethod", includes: "Steri-Strips" },
      },
      { name: "approximationAchieved", label: "Approximation Achieved", type: "select", options: YES_NO_OPTIONS },
      { name: "bleedingControlled", label: "Bleeding Controlled", type: "select", options: YES_NO_OPTIONS },
      { name: "dressingApplied", label: "Dressing Applied", type: "select", options: YES_NO_OPTIONS },
    ],
    summaryFields: [
      "location",
      "length",
      "closureMethod",
      "sutureSize",
      "sutureType",
      "numberOfSutures",
      "numberOfStaples",
      "glueType",
      "numberOfStrips",
      "approximationAchieved",
      "bleedingControlled",
      "dressingApplied",
    ],
  },

  "Staple Removal": {
    category: "Procedure",
    type: "Staple Removal",
    label: "Staple Removal",
    detailFields: [
      { name: "location", label: "Location", type: "text", placeholder: "e.g. scalp" },
      { name: "numberRemoved", label: "Number Removed", type: "text", placeholder: "e.g. 5" },
      { name: "woundIntact", label: "Wound Intact", type: "select", options: YES_NO_OPTIONS },
    ],
    summaryFields: ["location", "numberRemoved", "woundIntact"],
  },

  "Foreign Body Removal": {
    category: "Procedure",
    type: "Foreign Body Removal",
    label: "Foreign Body Removal",
    detailFields: [
      { name: "location", label: "Location", type: "text", placeholder: "e.g. L hand" },
      { name: "object", label: "Object", type: "text", placeholder: "e.g. splinter" },
      { name: "removedCompletely", label: "Removed Completely", type: "select", options: YES_NO_OPTIONS },
    ],
    summaryFields: ["location", "object", "removedCompletely"],
  },

  "Patient Reassessment": {
    category: "Assessment",
    type: "Patient Reassessment",
    label: "Patient Reassessment",
    detailFields: [
      { name: "condition", label: "Condition", type: "select", options: ["", "Improved", "Unchanged", "Worsened"] },
      { name: "assessment", label: "Assessment", type: "text", placeholder: "Brief reassessment notes" },
    ],
    summaryFields: ["condition", "assessment"],
  },

  "Neurological Assessment": {
    category: "Assessment",
    type: "Neurological Assessment",
    label: "Neurological Assessment",
    detailFields: [
      { name: "mentalStatus", label: "Mental Status", type: "select", options: ["", "Alert", "Verbal", "Pain", "Unresponsive", "Confused"] },
      { name: "pupils", label: "Pupils", type: "select", options: ["", "PERRL", "Unequal", "Sluggish", "Fixed", "Other"] },
      { name: "deficits", label: "Deficits Noted", type: "select", options: YES_NO_OPTIONS },
    ],
    summaryFields: ["mentalStatus", "pupils", "deficits"],
  },

  "Stroke Assessment": {
    category: "Assessment",
    type: "Stroke Assessment",
    label: "Stroke Assessment",
    detailFields: [
      { name: "facialDroop", label: "Facial Droop", type: "select", options: YES_NO_OPTIONS },
      { name: "armDrift", label: "Arm Drift", type: "select", options: YES_NO_OPTIONS },
      { name: "speechAbnormal", label: "Speech Abnormal", type: "select", options: YES_NO_OPTIONS },
      { name: "lastKnownWell", label: "Last Known Well", type: "text", placeholder: "e.g. 13:20" },
    ],
    summaryFields: ["facialDroop", "armDrift", "speechAbnormal", "lastKnownWell"],
  },

  "Fall Risk Assessment": {
    category: "Assessment",
    type: "Fall Risk Assessment",
    label: "Fall Risk Assessment",
    detailFields: [
      { name: "riskLevel", label: "Risk Level", type: "select", options: ["", "Low", "Moderate", "High"] },
      { name: "interventions", label: "Interventions", type: "multiSelect", options: ["Assisted Ambulation", "Wheelchair", "Staff Escort", "Fall Precautions", "Other"] },
    ],
    summaryFields: ["riskLevel", "interventions"],
  },

  "Medication Administration": {
  category: "Medication",
  type: "Medication Administration",
  label: "Medication Administration",
  detailFields: [
    {
      name: "medication",
      label: "Medication",
      type: "searchSelect",
      options: MEDICATION_OPTIONS,
      placeholder: "Start typing medication...",
    },
    {
      name: "dose",
      label: "Dose",
      type: "text",
      placeholder: "e.g. 4 mg",
    },
    {
      name: "route",
      label: "Route",
      type: "select",
      options: ["", "PO", "SL", "IN", "IM", "IV", "IO", "Neb", "Topical", "Other"],
    },
    {
      name: "indication",
      label: "Indication",
      type: "text",
      placeholder: "e.g. nausea, pain, allergic reaction",
    },
    {
      name: "response",
      label: "Response",
      type: "text",
      placeholder: "Patient response",
    },
  ],
  summaryFields: ["medication", "dose", "route", "indication", "response"],
},
};

export function getTreatmentOptionsByCategory(category = "") {
  return Object.keys(TREATMENT_SCHEMAS).filter(
    (key) => TREATMENT_SCHEMAS[key]?.category === category
  );
}

export const TREATMENT_TYPE_OPTIONS = Object.keys(TREATMENT_SCHEMAS);